import { useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import {
  HiHome,
  HiAcademicCap,
  HiShieldCheck,
  HiExclamationTriangle,
} from 'react-icons/hi2';
import { AppLayout, type NavItem } from '@themes/aegix';
import { Box, Center, Image, Spinner } from '@chakra-ui/react';
import { useColorMode } from '@/components/ui/color-mode';
import logoLight from '@/assets/aegix-one-logo-light.svg';
import logoDark from '@/assets/aegix-one-logo-dark.svg';
import { useIsAuthenticated, useIsHydrated, useUser, type AllauthUser } from '@aegix/auth-identity';
import {
  NotificationsDrawerProvider,
  type LocalNotification,
} from '@/contexts/NotificationsDrawerContext';
import { GeofenceLifecycleManager } from '@/components/GeofenceLifecycleManager';
import { RedirectToLogin, PostLoginRedirect } from '@/components/AuthRedirects';
import { PushNotificationsManager } from '@/push/PushNotificationsManager';
import { Toaster } from '@/components/ui/toaster';

import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

import Login from '@/pages/Login';
import Register from '@/pages/Register';
import VerifyEmail from '@/pages/VerifyEmail';
import AuthCallback from '@/pages/AuthCallback';
import AcceptInvite from '@/pages/AcceptInvite';
import ResetPassword from '@/pages/ResetPassword';
import Dashboard from '@/pages/Dashboard';
import Students from '@/pages/Students';
import Events from '@/pages/Events';
import EventDetail from '@/pages/EventDetail';
import Report from '@/pages/Report';
import { NotificationsDrawer } from '@/components/NotificationsDrawer';

function Logo() {
  const { colorMode } = useColorMode();
  const logoSrc = colorMode === 'dark' ? logoDark : logoLight;
  return (
    <Box px={4} py={4}>
      <Image src={logoSrc} alt="Aegix One" w="100%" />
    </Box>
  );
}

export default function App() {
  const { t, i18n } = useTranslation();
  const user = useUser<AllauthUser>();

  useEffect(() => {
    if (user?.language) {
      const cleanLang = user.language.split('-')[0];
      if (i18n.language !== cleanLang) {
        void i18n.changeLanguage(cleanLang);
      }
    }
  }, [user?.language, i18n]);

  const navItems: NavItem[] = [
    { label: t('nav.dashboard'), path: '/', icon: HiHome },
    { label: t('nav.myStudents'), path: '/students', icon: HiAcademicCap },
    { label: t('nav.events'), path: '/events', icon: HiShieldCheck },
    { label: t('nav.report'), path: '/report', icon: HiExclamationTriangle, hasDividerAbove: true },
  ];

  // `useIsHydrated` from `@aegix/auth-identity` flips to true once
  // boot-time reconciliation has decided which auth state to land in —
  // including the fetchMe round-trip when applicable.
  const isAuthenticated = useIsAuthenticated();
  const isHydrated = useIsHydrated();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [localNotifications, setLocalNotifications] = useState<LocalNotification[]>([]);

  const addLocalNotification = (
    notification: Omit<LocalNotification, 'id' | 'is_read' | 'created_at'>
  ) => {
    const newItem: LocalNotification = {
      ...notification,
      id: `local-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      is_read: false,
      created_at: new Date().toISOString(),
    };
    setLocalNotifications((prev) => [newItem, ...prev]);
  };

  const markLocalNotificationRead = (id: string) => {
    setLocalNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
  };

  if (!isHydrated) {
    return (
      <Center h="100vh" bg="surface.primary">
        <Spinner size="lg" />
      </Center>
    );
  }

  // Not authenticated — show login, register, verify, invite, and OAuth callback routes
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* A deep link hit while signed out (e.g. a cold push
            click opening /events/<id>) captures its target
            before bouncing to /login; PostLoginRedirect below (and
            AuthCallback for SSO) completes it after auth. */}
        <Route path="*" element={<RedirectToLogin />} />
      </Routes>
    );
  }

  return (
    <NotificationsDrawerProvider
      value={{
        open: () => setNotificationsOpen(true),
        localNotifications,
        addLocalNotification,
        markLocalNotificationRead,
      }}
    >
      <GeofenceLifecycleManager />
      {/* Authenticated-only mount — FCM registration (and the
          notification-permission prompt it may surface) is a deliberate
          post-login moment, and the manager needs the drawer context for
          foreground pushes. */}
      <PushNotificationsManager />
      <Toaster />

      <Routes>
        <Route
          element={
            <AppLayout
              logo={<Logo />}
              navItems={navItems}
              userMenuLocation="header"
            />
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="students" element={<Students />} />
          <Route path="events" element={<Events />} />
          <Route path="events/:eventId" element={<EventDetail />} />
          <Route path="report" element={<Report />} />
        </Route>
        {/* The password login flow doesn't navigate on success
            (useLoginForm relies on this re-render), so the authenticated
            /login route is where the preserved deep-link target — or the
            home default — resolves. */}
        <Route path="/login" element={<PostLoginRedirect />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
      </Routes>

      <NotificationsDrawer
        isOpen={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      />
    </NotificationsDrawerProvider>
  );
}

